import { FC } from 'react';
import { Icon, SocialMediaIcon } from '@kontentino/ui';
import { modalRoutes } from 'constants/routes';
import { useTranslation } from 'react-i18next';
import { faArrowRight } from '@fortawesome/pro-regular-svg-icons/faArrowRight';
import { TYPE } from '@kontentino/kontentino-constants/Pages';
import { useSocialLoginContext } from 'app/modules/socialLogin/context';
import { addQueryParams } from 'utils/url';

type NetworkCard = {
  pageType: number;
  iconType: 'facebook' | 'instagram' | 'linkedin';
  name: string;
  meta: string;
};

const AnalyticsNoProfilesEmptyState: FC = () => {
  const { t } = useTranslation();
  const socialLogin = useSocialLoginContext();

  const networks: NetworkCard[] = [
    {
      pageType: TYPE.FACEBOOK,
      iconType: 'facebook',
      name: 'Facebook',
      meta: t('analytics.noProfiles.pagesAndGroups'),
    },
    {
      pageType: TYPE.INSTAGRAM,
      iconType: 'instagram',
      name: 'Instagram',
      meta: t('analytics.noProfiles.businessOrCreator'),
    },
    {
      pageType: TYPE.LINKEDIN,
      iconType: 'linkedin',
      name: 'LinkedIn',
      meta: t('analytics.noProfiles.companyPages'),
    },
  ];

  const handleConnect = (pageType: number) => {
    socialLogin.authorize({
      pageType,
      redirectUrl: `${addQueryParams(
        window.location.href.replace(window.location.hash, ''),
        { type: pageType },
      )}#${modalRoutes.ASSOCIATE_POSTS_PROFILE}`,
    });
  };

  return (
    <div className="tw-flex tw-min-h-[600px] tw-items-center tw-justify-center tw-px-8 tw-py-16">
      <div className="tw-w-full tw-max-w-[760px] tw-text-center">
        <h2 className="tw-text-2xl tw-font-bold tw-tracking-tight tw-text-grayscale-180">
          {t('analytics.noProfiles.title')}
        </h2>
        <p className="tw-mx-auto tw-mb-10 tw-mt-3 tw-max-w-lg tw-text-md tw-leading-relaxed tw-text-grayscale-120">
          {t('analytics.noProfiles.description')}
        </p>

        <div className="tw-grid tw-grid-cols-1 tw-gap-4 md:tw-grid-cols-3">
          {networks.map((network) => (
            <button
              key={network.iconType}
              className="tw-group tw-relative tw-flex tw-cursor-pointer tw-flex-col tw-gap-4 tw-rounded-xl tw-border tw-border-grayscale-20 tw-bg-white tw-p-6 tw-text-left tw-transition-all tw-duration-200 hover:tw-border-primary-50 hover:tw-shadow-md active:tw-scale-[0.98]"
              onClick={() => handleConnect(network.pageType)}
              data-cy={`analytics_no-profiles_connect-${network.iconType}`}
            >
              <SocialMediaIcon
                type={network.iconType}
                variant="solid"
                size={36}
              />
              <div>
                <div className="tw-text-lg tw-font-semibold tw-text-grayscale-180">
                  {network.name}
                </div>
                <div className="tw-mt-1 tw-text-md tw-text-grayscale-100">
                  {network.meta}
                </div>
              </div>
              <div className="tw-transition-gap tw-flex tw-items-center tw-gap-1 tw-text-sm tw-font-semibold tw-text-primary-100 tw-duration-200 group-hover:tw-gap-2">
                {t('connect')}
                <Icon icon={faArrowRight} size={10} />
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AnalyticsNoProfilesEmptyState;
